import { useState, useEffect, useCallback } from 'react';
import { SmartTableColumn } from '../types';
import { tableStateManager, TableState } from './tableStateManager';

interface UseTableStateProps {
  resource: string;
  columns: SmartTableColumn[];
}

interface UseTableStateReturn {
  visibleColumns: string[];
  setVisibleColumns: (columns: string[]) => void;
  filters: Record<string, any>;
  setFilters: (filters: Record<string, any>) => void;
  sorters: Array<{
    field: string;
    order: 'asc' | 'desc';
  }>;
  setSorters: (
    sorters: Array<{ field: string; order: 'asc' | 'desc' }>
  ) => void;
  search: {
    value: string;
    fields: string[];
  };
  setSearch: (search: { value: string; fields: string[] }) => void;
  resetToDefault: () => void;
  clearState: () => void;
}

export const useTableState = ({
  resource,
  columns,
}: UseTableStateProps): UseTableStateReturn => {
  // Initialize state from storage
  const initialState = tableStateManager.getInitialState(
    resource,
    columns || []
  );

  const [visibleColumns, setVisibleColumnsState] = useState<string[]>(
    initialState.visibleColumns || []
  );

  const [filters, setFiltersState] = useState<Record<string, any>>(
    initialState.filters || {}
  );

  const [sorters, setSortersState] = useState<
    Array<{ field: string; order: 'asc' | 'desc' }>
  >(initialState.sorters || []);

  const [search, setSearchState] = useState<{
    value: string;
    fields: string[];
  }>(initialState.search || { value: '', fields: [] });

  // Flag to temporarily disable localStorage saving after clearing state
  const [isClearingState, setIsClearingState] = useState(false);

  // Save visible columns to storage
  const setVisibleColumns = useCallback(
    (visibleColumnKeys: string[]) => {
      setVisibleColumnsState(visibleColumnKeys);
      if (!isClearingState) {
        tableStateManager.setState(
          resource,
          { visibleColumns: visibleColumnKeys },
          columns
        );
      }
    },
    [resource, isClearingState, columns]
  );

  // Save filters to storage
  const setFilters = useCallback(
    (newFilters: Record<string, any>) => {
      setFiltersState(newFilters);
      if (!isClearingState) {
        tableStateManager.setState(resource, { filters: newFilters }, columns);
      }
    },
    [resource, isClearingState, columns]
  );

  // Save sorters to storage
  const setSorters = useCallback(
    (newSorters: Array<{ field: string; order: 'asc' | 'desc' }>) => {
      setSortersState(newSorters);
      if (!isClearingState) {
        tableStateManager.setState(resource, { sorters: newSorters }, columns);
      }
    },
    [resource, isClearingState, columns]
  );

  // Save search to storage
  const setSearch = useCallback(
    (newSearch: { value: string; fields: string[] }) => {
      setSearchState(newSearch);
      if (!isClearingState) {
        tableStateManager.setState(resource, { search: newSearch }, columns);
      }
    },
    [resource, isClearingState, columns]
  );

  // Reset to default state
  const resetToDefault = useCallback(() => {
    const defaultVisibleColumns = columns ? columns.map(col => col.key) : [];

    setVisibleColumnsState(defaultVisibleColumns);
    setFiltersState({});
    setSortersState([]);
    setSearchState({ value: '', fields: [] });

    // Clear stored state and save default
    tableStateManager.clearState(resource);
    tableStateManager.setState(
      resource,
      {
        visibleColumns: defaultVisibleColumns,
        filters: {},
        sorters: [],
        search: { value: '', fields: [] },
      },
      columns
    );
  }, [resource, columns]);

  // Clear all state
  const clearState = useCallback(() => {
    // Set flag to prevent localStorage saving during clearing
    setIsClearingState(true);

    // Clear stored state from localStorage
    tableStateManager.clearState(resource);

    // Reset local state to default values (but don't save to localStorage)
    const defaultVisibleColumns = columns ? columns.map(col => col.key) : [];

    // Directly update state without triggering localStorage save
    setVisibleColumnsState(defaultVisibleColumns);
    setFiltersState({});
    setSortersState([]);
    setSearchState({ value: '', fields: [] });

    // Re-enable localStorage saving immediately after state reset
    setIsClearingState(false);
  }, [resource, columns]);

  // Update state when columns change
  useEffect(() => {
    const currentState = tableStateManager.getState(resource);
    if (currentState && columns) {
      // Validate that all stored visible columns still exist
      const validColumns = columns.map(col => col.key);
      const validVisibleColumns = (currentState.visibleColumns || []).filter(
        (colKey: string) => validColumns.includes(colKey)
      );

      if (
        validVisibleColumns.length !==
        (currentState.visibleColumns || []).length
      ) {
        setVisibleColumns(validVisibleColumns);
      }
    }
  }, [columns, resource, setVisibleColumns]);

  return {
    visibleColumns,
    setVisibleColumns,
    filters,
    setFilters,
    sorters,
    setSorters,
    search,
    setSearch,
    resetToDefault,
    clearState,
  };
};
